import {
  ConsistencyScoreResultResponse,
  ConsistencyScoreControllerService,
  ConsistencyScoreGoalResponse,
  RequirementFulfillmentResponse,
  GoalRequirementResponse,
  TaskResponse,
  ApiError
} from "@9amhealth/openapi";
import { Blac, Cubit } from "blac-next";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { CONSISTENCY_SCORE_ARTICLE_URLS } from "src/constants/refillArticles";
import { EmployerId, PayerId } from "src/constants/payers";
import LoadingCubit, { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import TaskManagementBloc, {
  TaskKey,
  TaskResponseKnown
} from "../TaskManagementBloc/TaskManagementBloc";
import translate from "src/lib/translate";
import { dateLocalString } from "src/lib/date";
import { featureFlags } from "src/lib/featureFlags";
import { StorageController } from "../StorageBloc/StorageBloc";
import { userState } from "../state";
import { onUserSessionReset } from "src/lib/onUserSessionReset";

export const ConsistencyScoreStatus = ConsistencyScoreResultResponse.status;
export type ConsistencyScoreType = ConsistencyScoreResultResponse.type;
export const RequirementFulfillmentType = RequirementFulfillmentResponse.type;

export type ConsistencyScoreGoalEngagementType =
  ConsistencyScoreGoalResponse.engagementGoalType;
export type RequirementFulfillmentGoalType = GoalRequirementResponse.goalType;

export interface ConsistencyScoreGoal {
  engagementGoalType: ConsistencyScoreGoalEngagementType;
  title: string;
  count: number;
  maxCount: number;
}

export interface GoalRequirement {
  goalType: RequirementFulfillmentGoalType;
  minCount: number;
}

export interface RequirementFulfillment {
  type: keyof typeof RequirementFulfillmentType;
  threshold: number;
  goalRequirements: GoalRequirement[];
  fulfilled?: boolean;
}

export interface ConsistencyScore {
  score: number;
  status: keyof typeof ConsistencyScoreStatus;
  goals: ConsistencyScoreGoal[];
  type: ConsistencyScoreType;
  requirementFulfillments: RequirementFulfillment[];
}

export interface ConsistencyScoreState {
  data?: ConsistencyScore;
  loading: boolean;
  error: string | null;
  glp1Requirement?: RequirementFulfillment;
  isGlp1RequirementReached: boolean;
}

export default class ConsistencyScoreCubit extends Cubit<ConsistencyScoreState> {
  static keepAlive = true;
  private static readonly CACHE_KEY = "consistencyScore";

  constructor() {
    super({
      data: undefined,
      loading: false,
      error: null,
      glp1Requirement: undefined,
      isGlp1RequirementReached: false
    });

    onUserSessionReset(this.reset);
    void this.fetchConsistencyScore();
  }

  private readonly reset = (): void => {
    this.emit({
      data: undefined,
      loading: false,
      error: null,
      glp1Requirement: undefined,
      isGlp1RequirementReached: false
    });
  };

  private loadFromCache(): ConsistencyScore | null {
    return StorageController.getItemParsed<ConsistencyScore>(
      ConsistencyScoreCubit.CACHE_KEY
    );
  }

  private saveToCache(data: ConsistencyScore): void {
    StorageController.setItem(
      ConsistencyScoreCubit.CACHE_KEY,
      JSON.stringify(data)
    );
  }

  private applyData(data: ConsistencyScore): void {
    this.patch({
      data,
      loading: false,
      error: null
    });
    this.setRequirementState(data);
  }

  private readonly fetchFromApi = async (): Promise<void> => {
    try {
      const { data } =
        await ConsistencyScoreControllerService.getConsistencyScore();

      const parsedData = this.parseConsistencyScore(data);
      this.saveToCache(parsedData);
      this.applyData(parsedData);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to fetch consistency score";
      this.patch({
        loading: false,
        error: errorMessage
      });
      if (!(error instanceof ApiError) || error.status !== 404) {
        reportErrorSentry(error);
      }
    }
  };

  public readonly fetchConsistencyScore = async (): Promise<void> => {
    // Wait for backend flags to settle so a constructor-time fetch (which can run before
    // loadBackendFlags resolves) doesn't short-circuit on the not-yet-loaded flag.
    await featureFlags.ready;

    if (!featureFlags.consistencyScoreEnabled) {
      return;
    }

    const loadingState = Blac.getBloc(LoadingCubit);
    loadingState.start(LoadingKey.consistencyScore);

    this.patch({
      loading: true,
      error: null
    });

    const cached = this.loadFromCache();

    if (cached) {
      this.applyData(cached);
      // Sync fresh data in the background
      void this.fetchFromApi();
    } else {
      await this.fetchFromApi();
    }

    loadingState.finish(LoadingKey.consistencyScore);
  };

  public readonly clearData = (): void => {
    StorageController.removeItem(ConsistencyScoreCubit.CACHE_KEY);
    this.patch({
      data: undefined,
      loading: false,
      error: null,
      glp1Requirement: undefined,
      isGlp1RequirementReached: false
    });
  };

  public readonly refresh = (): void => {
    void this.fetchConsistencyScore();
  };

  private readonly parseGoalRequirement = (
    goalRequirement: GoalRequirementResponse
  ): GoalRequirement => {
    return {
      goalType: goalRequirement.goalType,
      minCount: goalRequirement.minCount
    };
  };

  private readonly parseConsistencyScoreGoal = (
    goal: ConsistencyScoreGoalResponse
  ): ConsistencyScoreGoal => {
    return {
      engagementGoalType: goal.engagementGoalType,
      title: goal.title,
      count: goal.count,
      maxCount: goal.maxCount
    };
  };

  private readonly parseRequirementFulfillment = (
    fulfillment: RequirementFulfillmentResponse
  ): RequirementFulfillment => {
    return {
      type: fulfillment.type,
      threshold: fulfillment.threshold,
      fulfilled: fulfillment.fulfilled,
      goalRequirements: fulfillment.goalRequirements.map(
        this.parseGoalRequirement
      )
    };
  };
  private readonly parseConsistencyScore = (
    response: ConsistencyScoreResultResponse
  ): ConsistencyScore => {
    return {
      score: response.score,
      requirementFulfillments: response.requirementFulfillments.map(
        this.parseRequirementFulfillment
      ),
      type: response.type,
      status: response.status,
      goals: response.goals.map(this.parseConsistencyScoreGoal)
    };
  };

  private readonly setRequirementState = (data?: ConsistencyScore) => {
    const glp1Requirement = data?.requirementFulfillments.find(
      (req) => req.type === RequirementFulfillmentType.GLP1_REFILL
    );

    const isGlp1RequirementReached =
      Boolean(glp1Requirement?.threshold) &&
      typeof data?.score === "number" &&
      data.score >= (glp1Requirement?.threshold ?? 0);

    this.patch({
      glp1Requirement,
      isGlp1RequirementReached
    });
  };

  private readonly isRefillSurveyDateReached = (
    refillSurveyTask?: TaskResponseKnown
  ): boolean => {
    if (!refillSurveyTask) {
      return false;
    }

    const now = new Date();
    const availableFrom = refillSurveyTask.availableFrom
      ? new Date(refillSurveyTask.availableFrom)
      : null;
    return !availableFrom || now >= availableFrom;
  };

  /**
   * The set of task slugs that block the refill survey for Georgia SHBP members.
   * Any non-terminal task from this list will surface the "Update Your Lab Record"
   * tile in the PrepareRefillDialog above the refill survey tile.
   *
   * Defined as a getter (not a static field) to avoid circular-dependency
   * initialisation issues: TaskKey would be undefined at class-definition time
   * because TaskManagementBloc and ConsistencyScoreCubit import each other.
   */
  private static get blockingLabTaskSlugs(): readonly TaskKey[] {
    return [
      TaskKey.INITIAL_LAB_QUESTIONNAIRE,
      TaskKey.COMPLETE_INITIAL_LAB_ORDER,
      TaskKey.COMPLETE_RECURRING_LAB_ORDER,
      TaskKey.UPLOAD_LABS
    ];
  }

  /**
   * Returns the first non-terminal blocking lab task for Georgia SHBP members.
   */
  public readonly isUploadLabTaskVisible = (): {
    visible: boolean;
    task: TaskResponseKnown | undefined;
  } => {
    if (!userState.isStateOfGeorgiaMember) {
      return { visible: false, task: undefined };
    }

    const { tasks } = Blac.getBloc(TaskManagementBloc).state;

    const isNonTerminal = (t: TaskResponseKnown) =>
      t.status !== TaskResponse.status.COMPLETED &&
      t.status !== TaskResponse.status.SKIPPED;

    const blockingTask = ConsistencyScoreCubit.blockingLabTaskSlugs
      .map((slug) => tasks.find((t: TaskResponseKnown) => t.slug === slug))
      .find((t) => t !== undefined && isNonTerminal(t));

    const refillSurveyTask = tasks.find(
      (t: TaskResponseKnown) => t.slug === TaskKey.GLP1_REFILL_SURVEY
    );

    const isRefillSurveyLocked =
      refillSurveyTask?.status === TaskResponse.status.LOCKED;

    if (isRefillSurveyLocked) {
      return {
        visible: blockingTask !== undefined,
        task: blockingTask
      };
    }

    return { visible: false, task: undefined };
  };

  public get articleUrl(): string {
    if (userState.isPayerMember(PayerId.EVERNORTH)) {
      return CONSISTENCY_SCORE_ARTICLE_URLS.EVERNORTH;
    }
    if (userState.isEmployerMember(EmployerId.BANK_OF_NEW_YORK)) {
      return CONSISTENCY_SCORE_ARTICLE_URLS.BNY;
    }
    if (userState.isEmployerMember(EmployerId.KWIK_TRIP)) {
      return CONSISTENCY_SCORE_ARTICLE_URLS.KWIK_TRIP;
    }
    if (this.state.data?.type === ConsistencyScoreResultResponse.type.HWJ) {
      return CONSISTENCY_SCORE_ARTICLE_URLS.HWJ;
    }
    return CONSISTENCY_SCORE_ARTICLE_URLS.NON_HWJ;
  }

  // Determine the task description based on status and available date
  public readonly getGlp1RefillTaskDescription = (
    refillSurveyTask?: TaskResponseKnown
  ): string => {
    if (!refillSurveyTask) {
      return "";
    }

    const { glp1Requirement, data } = this.state;
    const isAvailableDateReached =
      this.isRefillSurveyDateReached(refillSurveyTask);

    if (refillSurveyTask.status === "LOCKED") {
      if (this.isUploadLabTaskVisible().visible) {
        return translate("prepareRefillDialog.updateLabRecord.description");
      }

      if (isAvailableDateReached) {
        // Locked because of consistency score not reached
        // Calculate how many more points are needed to unlock
        if (glp1Requirement?.threshold && data?.score !== undefined) {
          const remainingPoints = glp1Requirement.threshold - data.score;
          if (remainingPoints > 0) {
            return translate(
              "prepareRefillDialog.refillSurvey.description.points",
              {
                points: remainingPoints
              }
            );
          } else {
            return "";
          }
        }

        // Fallback if calculation fails
        return translate(
          "prepareRefillDialog.refillSurvey.description.pointsFallback"
        );
      } else {
        // Locked because not yet available
        const formattedDate = dateLocalString(refillSurveyTask.availableFrom, {
          dateStyle: "medium",
          timeStyle: "short"
        });
        return translate(
          "prepareRefillDialog.refillSurvey.description.unlocksAt",
          {
            date: formattedDate
          }
        );
      }
    }

    return "";
  };
}
